/*
 * ARCHIVED MESSAGES → TURNS, once, for every surface that reads a transcript.
 *
 * The archive stores the AI-SDK parts model: prose, thinking, tool dispatches and their results, the
 * turn's own measurements, the trees it started — as separate rows, in the order they happened. What
 * a reader wants is TURNS: a bubble carrying what that turn said and what it ran. The fold between
 * those two shapes is not obvious (see the tool-attachment rule below), and it was written inside
 * `AiManager.hydrate` where only a live `Conversation` could reach it.
 *
 * It moved here the moment a SECOND reader appeared. A transcript keyed by run id renders the same
 * archived rows for a sub-agent that nobody is chatting with, and the alternative was a second fold
 * — two mappings of one wire format, agreeing today, drifting the first time either is taught about
 * a new part type. One fold, two callers, and the rule about which turn owns a tool call is stated
 * in one place.
 *
 * Pure and DOM-free on purpose: it takes rows and returns rows, so it is testable without a store,
 * a socket, or a component.
 */
import { isReturnProse } from '@vitesse/agent-loop';
import { joinProse } from './conversation';
import { textOf, reasoningOf, reasoningMsOf, toolsOf, filesOf, telemetryOf, spawnedRootsOf } from './manager';
/**
 * Fold archived rows into turns.
 *
 * TOOL CALLS ATTACH TO THE ANSWER THEY PRODUCED, rather than becoming turns of their own. The
 * archive stores a dispatch and its result as separate messages, so replaying them one-per-row puts
 * unattributed fragments between a question and its answer. They are collected as they are passed
 * and handed to the next assistant turn that actually says something, which reconstructs the live
 * shape: the bubble is the turn, and it carries what the turn ran. Anything still pending at the end
 * belongs to the last turn, whatever it was — a thread that ends on an unresolved tool still ran it,
 * and the record should say so.
 *
 * A ROW WITH NO PROSE AND NO THINKING SAID NOTHING, and dropping it is load-bearing twice over. The
 * parts vocabulary is wider than prose — a message may be entirely `tool-<name>` parts, or a `data-*`
 * snapshot of what the AI saw — and `textOf` keeps only `text` parts, so every one of those folds to
 * the empty string. Kept, they render as the empty bubbles a reopened thread used to stack at the
 * top after any turn that called a tool. The worse half is invisible: folded turns are also primed
 * into the store's mirror, so those empty strings go back to the model as history — a prompt that has
 * been told the assistant repeatedly answered with nothing. It is right rather than merely tidy: a
 * tool call is not a turn in the conversation, it is something that happened inside one.
 */
/**
 * ONE ROW PER CALL, not one per phase.
 *
 * A tool call is recorded in two writes — the dispatch (name + arguments) and the outcome (state +
 * output) — because the second cannot be known when the first happens. Read back, that is two parts
 * carrying the SAME `toolCallId`, and appending them produced two cards for one call: the first
 * showing arguments and no result, the second showing a result and "No arguments", both opening
 * together because they shared an id.
 *
 * Merged by id, later phases winning field by field. Field-by-field rather than wholesale because
 * neither phase is complete on its own: the arguments exist only in the first, the outcome only in
 * the second, and taking either record whole loses the other half.
 */
function mergeTools(into, incoming) {
    const out = [...into];
    for (const t of incoming) {
        const at = out.findIndex((x) => x.id === t.id);
        if (at < 0) {
            out.push(t);
            continue;
        }
        const prev = out[at];
        out[at] = {
            ...prev,
            ...t,
            // The dispatch is the only phase that carries them; an outcome-only record must not blank them.
            argumentsJson: t.argumentsJson || prev.argumentsJson,
            name: t.name || prev.name,
            // A settled outcome replaces an unsettled one, never the other way round.
            state: t.state === 'unknown' && prev.state !== 'unknown' ? prev.state : t.state,
        };
    }
    return out;
}
export function foldTranscript(messages) {
    /*
     * ONE TURN PER EXCHANGE, IN THE ORDER IT HAPPENED.
     *
     * The archive is chronological: a thinking row, a tool-call row, its result, a prose row, another
     * call… This fold used to collapse that into buckets — attaching every tool it had seen to the
     * next row that SPOKE — which (a) lost the order entirely, so a call made after a sentence drew
     * above it, and (b) made every prose row its own turn, so a multi-step agent run was a stack of
     * turns with an action row and a margin between each, and rows MOVED between turns as new ones
     * landed. Both were reported, repeatedly, as the thread being wrong.
     *
     * Now every run of non-user rows folds into ONE assistant turn whose `parts` are those rows in
     * order: thinking, the calls it led to, what was said, the next calls. A user row closes the
     * group. The aggregates (`text`, `reasoning`, `tools`) are derived from the sequence so nothing
     * reading the whole turn disagrees with the order it is drawn in.
     */
    const turns = [];
    let group = null;
    let proseSeq;
    const finish = () => {
        if (!group)
            return;
        group.text = joinProse(group.parts.filter((p) => p.kind === 'text').map((p) => p.text));
        const thought = group.parts.filter((p) => p.kind === 'reasoning');
        if (thought.length) {
            group.reasoning = thought.map((p) => p.text).join('\n\n');
            const ms = thought.reduce((a, p) => a + (p.ms ?? 0), 0);
            if (ms)
                group.reasoningMs = ms;
        }
        // The seq of the LAST PROSE row when there is one — a fork from a turn begins after everything
        // it said — else of its last row.
        if (proseSeq !== undefined)
            group.seq = proseSeq;
        if (!group.tools.length)
            delete group.tools;
        group = null;
        proseSeq = undefined;
    };
    for (const m of messages) {
        if (m.role === 'user') {
            finish();
            const text = textOf(m.content);
            if (!text)
                continue;
            // What the person attached, back on their turn — the chip they saw when they sent it.
            const attached = filesOf(m.content);
            turns.push({
                role: 'user',
                text,
                ...(attached.length ? { files: attached } : {}),
                ...(typeof m.seq === 'number' ? { seq: m.seq, rows: [m.seq] } : {}),
                ...(m.fromRunId ? { fromRunId: m.fromRunId } : {}),
                ...(m.fromRunId && isReturnProse(text) ? { returned: true } : {}),
            });
            continue;
        }
        if (!group) {
            group = { role: 'assistant', text: '', parts: [], tools: [], rows: [] };
            turns.push(group);
            if (typeof m.seq === 'number')
                group.seq = m.seq;
        }
        if (typeof m.seq === 'number')
            group.rows.push(m.seq);
        // Thinking first: it led to whatever else this row carries.
        const reasoning = reasoningOf(m.content);
        if (reasoning) {
            const ms = reasoningMsOf(m.content);
            group.parts.push({ kind: 'reasoning', text: reasoning, ...(ms !== undefined ? { ms } : {}) });
        }
        // A call is a part the first time it is seen; its result only settles the row it already has.
        const before = group.tools.map((t) => t.id);
        group.tools = mergeTools(group.tools, toolsOf(m.content));
        for (const t of group.tools)
            if (!before.includes(t.id))
                group.parts.push({ kind: 'tool', id: t.id });
        for (const r of spawnedRootsOf(m.content)) {
            group.spawnedRoots ??= [];
            if (!group.spawnedRoots.includes(r))
                group.spawnedRoots.push(r);
        }
        // By sha, because the store is content-addressed: the same picture referenced twice in a turn
        // is one picture, and drawing it twice would read as two.
        for (const f of filesOf(m.content)) {
            group.files ??= [];
            if (!group.files.some((x) => x.sha256 === f.sha256))
                group.files.push(f);
        }
        const text = textOf(m.content);
        if (text) {
            group.parts.push({ kind: 'text', text });
            if (typeof m.seq === 'number')
                proseSeq = m.seq;
        }
        /*
         * A ROW CARRYING THE TURN'S MEASUREMENTS IS THE TURN'S LAST ROW.
         *
         * Telemetry is written once, on the row that ends an answer — a person's thread stamps its final
         * prose with it; an agent's step rows carry none. So it is a boundary the record itself draws,
         * and the fold honours it: what follows is a NEW answer, not more of this one. Without it, three
         * "try again"s on one question folded into a single turn reading as one answer that restarted
         * itself twice, and a stopped answer swallowed the fresh one written after it.
         */
        const telemetry = telemetryOf(m.content);
        if (telemetry) {
            group.telemetry = telemetry;
            finish();
        }
    }
    finish();
    return turns;
}
//# sourceMappingURL=transcript-fold.js.map